/**
 * OwnerRoutes — <Routes> du shell propriétaire (Sprint routeur).
 *   - Lot 0+1 : pages SANS paramètre (extrait d'App.jsx pour rester sous 500 L).
 *   - Lot 2   : pages PARAMÉTRÉES (/horses/:id[…], /compare, /sessions filtrée)
 *     via les wrappers reload-by-id de ./ownerParamRoutes (deep-link/refresh/back).
 *
 * ISO-COMPORTEMENT : chaque <Route> rend exactement le même composant avec les
 * mêmes props que l'ancien switch `currentPage` d'App.jsx. Les transitions vers
 * une page routée passent par navigate (ici, couche routing) ou goRouted (pages
 * sans paramètre) au lieu de setCurrentPage seul.
 *
 * Composants enfants AGNOSTIQUES du routeur : ils gardent leurs props onNavigate/
 * onBack ; c'est ici qu'on mappe vers navigate. Boutons Retour → navigate(parent)
 * FIXE (jamais -1). /sessions/:id n'est PAS routée ici : branche dédiée d'App.jsx
 * (layout sans bannières, Option 1).
 */
import React, { lazy } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { PAGES } from '../constants/pages';
import { PAGE_TO_PATH } from './ownerRouteMap';
import { isGuestSignupRole } from '../utils/trusteePermissions';
import SharedHorses from '../horses/SharedHorses';
import HorseList from '../horses/HorseList';
import {
  HorseDetailRoute, HorseHealthRoute, HorseLiveRoute, CompareRoute, SessionListRoute,
} from './ownerParamRoutes';

const CsvUploadPage        = lazy(() => import('../sessions/CsvUploadPage'));
const HorseDeclarationForm = lazy(() => import('../horses/HorseDeclarationForm'));
const UserProfile          = lazy(() => import('../settings/UserProfile'));
const AdminDashboard       = lazy(() => import('../admin/AdminDashboard'));
const FeedbackPage         = lazy(() => import('../feedback/FeedbackPage'));
const FAQPage              = lazy(() => import('../settings/FAQPage'));
const NoticePage           = lazy(() => import('../settings/NoticePage'));
// Pages légales (Lot 3) — rendu IN-SHELL (authentifié). Mêmes composants que le
// court-circuit public d'App.jsx, mais avec header/AppNav + Retour → /account.
// CONTENU FR inchangé (track 1.11). CGU : pas de onBack (iso — l'ancien switch
// owner n'en posait pas). /support est aussi exposée publiquement (déconnecté).
const CGUPage              = lazy(() => import('../settings/CGUPage'));
const MentionsPage         = lazy(() => import('../settings/MentionsPage'));
const PrivacyPage          = lazy(() => import('../settings/PrivacyPage'));
const CookiesPage          = lazy(() => import('../settings/CookiesPage'));
const ModerationPage       = lazy(() => import('../settings/ModerationPage'));
const CGVPage              = lazy(() => import('../settings/CGVPage'));
const SupportPage          = lazy(() => import('../settings/SupportPage'));

/**
 * @param s          état applicatif (useAppState)
 * @param onNavigate (page) => handleNavigate + navigate si routée (barre de nav)
 * @param goRouted   (page) => setCurrentPage + navigate-si-besoin (transitions bespoke)
 * @param backBtn    render-prop du bouton Retour d'App.jsx (route /horses/:id/live)
 */
export default function OwnerRoutes({ s, onNavigate, goRouted, backBtn }) {
  const navigate = useNavigate();
  return (
    <Routes>
      <Route path="/import" element={
        <CsvUploadPage horses={[...s.horses, ...(s.sharedHorses || [])]} selectedHorse={s.selectedHorse} sensorType={s.sensorType} setSensorType={s.setSensorType} onSubmit={s.handleFileUpload} darkMode={s.darkMode} uploading={s.uploading} uploadError={s.uploadError} setUploadError={s.setUploadError} currentUser={s.currentUser} getShareRole={s.getShareRole} />
      } />

      <Route path="/sessions" element={<SessionListRoute s={s} onNavigate={onNavigate} />} />

      <Route path="/compare" element={<CompareRoute s={s} />} />

      <Route path="/horses" element={
        <>
          <SharedHorses darkMode={s.darkMode}
            onViewSharedHorse={(id, role, acceptedAt) => {
              s.setShareRoleForHorse(id, role, acceptedAt);
              navigate('/horses/' + id);
            }}
            onViewSessions={(id, role, acceptedAt) => {
              s.setShareRoleForHorse(id, role, acceptedAt);
              navigate('/sessions?horse=' + id);
            }}
            onViewLive={(horse) => navigate('/horses/' + horse._id + '/live')}
            onViewHealth={(horse) => navigate('/horses/' + horse._id + '/health')}
            onEdit={(id) => s.handleEditHorse(id)}
            onUploadCSV={(id, role, acceptedAt) => {
              s.setSelectedHorse(id);
              s.setShareRoleForHorse(id, role, acceptedAt);
              goRouted(PAGES.UPLOAD);
            }}
          />
          {(!isGuestSignupRole(s.currentUser) || s.horses.length > 0) && (
            <HorseList horses={s.horses} darkMode={s.darkMode}
              onViewData={(id) => navigate('/sessions?horse=' + id)}
              onUploadCSV={(id) => { s.setSelectedHorse(id); goRouted(PAGES.UPLOAD); }}
              onEdit={s.handleEditHorse} onDelete={s.handleDeleteHorse}
              onHealth={(id) => navigate('/horses/' + id + '/health')}
              onViewDetail={(id) => navigate('/horses/' + id)}
              onLive={(id) => navigate('/horses/' + id + '/live')}
            />
          )}
        </>
      } />

      {/* Pages chevaux paramétrées (Lot 2) — reload-by-id dans ownerParamRoutes.
          react-router v6 classe par spécificité : /horses/new et /horses/:id/*
          priment sur /horses/:id nu, indépendamment de l'ordre de déclaration. */}
      <Route path="/horses/new" element={
        <HorseDeclarationForm currentUser={s.currentUser} darkMode={s.darkMode}
          onSuccess={() => { s.loadHorses(); goRouted(PAGES.HORSES); }} />
      } />
      <Route path="/horses/:id/health" element={<HorseHealthRoute s={s} />} />
      <Route path="/horses/:id/live" element={<HorseLiveRoute s={s} backBtn={backBtn} />} />
      <Route path="/horses/:id" element={<HorseDetailRoute s={s} />} />

      <Route path="/account" element={
        <UserProfile currentUser={s.currentUser} onLogout={s.handleLogout} darkMode={s.darkMode} onNavigate={onNavigate} />
      } />

      <Route path="/admin" element={
        <AdminDashboard darkMode={s.darkMode} currentUser={s.currentUser} onViewSession={(id) => navigate('/sessions/' + id)} />
      } />

      <Route path="/feedback" element={<FeedbackPage darkMode={s.darkMode} />} />

      <Route path="/faq" element={<FAQPage darkMode={s.darkMode} onBack={() => navigate('/account')} />} />

      <Route path="/notice" element={
        <NoticePage darkMode={s.darkMode} onBack={() => goRouted(PAGES.PROFILE)} />
      } />

      {/* Pages légales (Lot 3) — IN-SHELL, accès authentifié (depuis UserProfile).
          Retour → /account (= ancien goRouted(PROFILE)). Vague A : CGU câblée
          comme les autres (onBack → /account) — l'asymétrie « CGU sans retour »
          in-shell est levée ; le rendu PUBLIC de CGU garde son retour → / (reload). */}
      <Route path="/legal/mentions"   element={<MentionsPage   darkMode={s.darkMode} onBack={() => navigate('/account')} />} />
      <Route path="/legal/cgu"        element={<CGUPage        darkMode={s.darkMode} onBack={() => navigate('/account')} />} />
      <Route path="/legal/privacy"    element={<PrivacyPage    darkMode={s.darkMode} onBack={() => navigate('/account')} />} />
      <Route path="/legal/cookies"    element={<CookiesPage    darkMode={s.darkMode} onBack={() => navigate('/account')} />} />
      <Route path="/legal/moderation" element={<ModerationPage darkMode={s.darkMode} onBack={() => navigate('/account')} />} />
      <Route path="/legal/cgv"        element={<CGVPage        darkMode={s.darkMode} onBack={() => navigate('/account')} />} />
      <Route path="/support"          element={<SupportPage    darkMode={s.darkMode} onBack={() => navigate('/account')} />} />

      {/* Atterrissage : "/" → page logique courante (préserve LIST vs HORSES de handleLogin) */}
      <Route path="/" element={<Navigate to={PAGE_TO_PATH[s.currentPage] || '/sessions'} replace />} />
      {/* Filet : tout chemin non routé retombe sur Sessions */}
      <Route path="*" element={<Navigate to="/sessions" replace />} />
    </Routes>
  );
}
